// Dependencies
import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import * as zod from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

// Actions
import { loginAction } from "@/actions/auth/login";

// Hooks
import { useToast } from "@/hooks/use-toast";

// Lib
import { LoginSchema } from "@/lib/zod";

// Interfaces
import type { LoginController } from "../interfaces/login.interface";

const useLoginController = (): LoginController => {
  const [isPending, startTransition] = useTransition();
  const [captchaResolved, setCaptchaResolved] = useState<string | null>(null);

  const router = useRouter();
  const { toast } = useToast();

  const handleSubmit = async (values: zod.infer<typeof LoginSchema>) => {
    startTransition(async () => {
      const res = await loginAction(values);

      if (res.success) {
        router.push("/auth/dashboard");
      } else {
        router.push("/accounts/login" + "?success=" + res.success);

        toast({
          title: "ERROR",
          description:
            "Hubo un error intentando acceder a tu cuenta. Verifica tus datos.",
          variant: "destructive",
        });
      }
    });
  };

  const handleCaptcha = (captcha: string | null) => {
    setCaptchaResolved(captcha);
  };

  const form = useForm<zod.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      correo: "",
      pwd: "",
    },
  });

  return { isPending, form, handleSubmit, handleCaptcha, captchaResolved };
};

export default useLoginController;
